import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  FitnessCenter as FitnessCenterIcon,
  Save as SaveIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  Shuffle as ShuffleIcon,
  Print as PrintIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { ApiError } from '../../services/api';
import { ExerciseService } from '../../services/exerciseService';
import { ProgramDayService } from '../../services/programDayService';
import { ProgramExerciseService } from '../../services/programExerciseService';
import { ProgramPeriodizationService } from '../../services/programPeriodizationService';
import { RotationalTrainingService } from '../../services/rotationalTrainingService';
import type {
  Exercise,
  ProgramExercise,
  ProgramPeriodizationOverview,
  RotationalConfig,
  TrainingProgram,
} from '../../services/types';
import MuscleLoadVisualization from '../MuscleLoadVisualization';
import ProgramPrintView from './ProgramPrintView';

interface ProgramBuilderV2Props {
  program: TrainingProgram;
  onProgramUpdate?: () => void;
  onClose?: () => void;
}

interface PlannerExercise extends ProgramExercise {
  clientKey: string;
  isNew?: boolean;
}

interface PlannerDay {
  dayNumber: number;
  name: string;
  weekday: number | null;
  exercises: PlannerExercise[];
  rotationConfig: RotationalConfig | null;
}

interface ExerciseEditorState {
  dayNumber: number;
  exerciseKey: string;
}

interface RotationEditorState {
  dayNumber: number;
  rotationName: string;
  rotationLabels: string[];
}

const PROGRAM_EXERCISES_UPDATED_EVENT = 'program-exercises-updated';

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Без ден' },
  { value: 1, label: 'Понеделник' },
  { value: 2, label: 'Вторник' },
  { value: 3, label: 'Сряда' },
  { value: 4, label: 'Четвъртък' },
  { value: 5, label: 'Петък' },
  { value: 6, label: 'Събота' },
  { value: 7, label: 'Неделя' },
];

const ROTATION_COLORS = ['#1d4ed8', '#c2410c', '#15803d', '#7c3aed', '#b45309', '#0f766e'];

const createClientKey = (seed: string | number) =>
  `${seed}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const cloneDays = (days: PlannerDay[]): PlannerDay[] =>
  days.map((day) => ({
    ...day,
    exercises: day.exercises.map((exercise) => ({ ...exercise, exercise: { ...exercise.exercise } })),
    rotationConfig: day.rotationConfig
      ? { ...day.rotationConfig, rotationLabels: [...day.rotationConfig.rotationLabels] }
      : null,
  }));

const normalizeSupersetGroups = (exercises: PlannerExercise[]): PlannerExercise[] => {
  const indexesByGroup = new Map<string, number[]>();

  exercises.forEach((exercise, index) => {
    if (!exercise.supersetGroup) {
      return;
    }

    indexesByGroup.set(exercise.supersetGroup, [
      ...(indexesByGroup.get(exercise.supersetGroup) || []),
      index,
    ]);
  });

  const invalidGroups = new Set<string>();
  indexesByGroup.forEach((indexes, group) => {
    const isContiguous = indexes.every((index, position) => position === 0 || index === indexes[position - 1] + 1);
    if (indexes.length < 2 || !isContiguous) {
      invalidGroups.add(group);
    }
  });

  if (invalidGroups.size === 0) {
    return exercises;
  }

  return exercises.map((exercise) =>
    exercise.supersetGroup && invalidGroups.has(exercise.supersetGroup)
      ? { ...exercise, supersetGroup: null }
      : exercise
  );
};

const buildDaysFromResponse = (params: {
  exercisesByDay: Record<number, ProgramExercise[]>;
  dayNames?: Record<number, string>;
  dayWeekdays?: Record<number, number>;
  rotationsByDay: Record<number, RotationalConfig>;
}): PlannerDay[] => {
  const daySet = new Set<number>();
  Object.keys(params.exercisesByDay).forEach((key) => daySet.add(Number(key)));
  Object.keys(params.dayNames || {}).forEach((key) => daySet.add(Number(key)));
  Object.keys(params.dayWeekdays || {}).forEach((key) => daySet.add(Number(key)));
  Object.keys(params.rotationsByDay).forEach((key) => daySet.add(Number(key)));

  if (daySet.size === 0) {
    daySet.add(1);
  }

  return Array.from(daySet)
    .sort((a, b) => a - b)
    .map((dayNumber) => ({
      dayNumber,
      name: params.dayNames?.[dayNumber] || `Day ${dayNumber}`,
      weekday: params.dayWeekdays?.[dayNumber] ?? null,
      rotationConfig: params.rotationsByDay[dayNumber] ?? null,
      exercises: [...(params.exercisesByDay[dayNumber] || [])]
        .sort((a, b) => a.orderInSession - b.orderInSession)
        .map((exercise) => ({
          ...exercise,
          clientKey: `saved-${exercise.id}`,
        })),
    }));
};

const buildSnapshot = (days: PlannerDay[]) =>
  days.map((day) => ({
    dayNumber: day.dayNumber,
    name: day.name.trim(),
    weekday: day.weekday,
    rotationConfig: day.rotationConfig
      ? {
          rotationName: day.rotationConfig.rotationName.trim(),
          rotationLabels: day.rotationConfig.rotationLabels.map((label) => label.trim()),
        }
      : null,
    exercises: day.exercises.map((exercise, index) => ({
      persistedId: exercise.isNew ? null : exercise.id,
      clientKey: exercise.clientKey,
      exerciseId: exercise.exercise.id,
      dayOfWeek: day.dayNumber,
      orderInSession: index + 1,
      targetSets: exercise.targetSets ?? null,
      targetReps: exercise.targetReps ?? null,
      targetWeight: exercise.targetWeight ?? null,
      targetDuration: exercise.targetDuration ?? null,
      targetDistance: exercise.targetDistance ?? null,
      notes: exercise.notes ?? '',
      supersetGroup: exercise.supersetGroup ?? null,
      rotationIndexes: exercise.rotationIndexes ?? null,
      isFocused: exercise.isFocused ?? false,
    })),
  }));

const formatWeekday = (weekday: number | null) =>
  WEEKDAY_OPTIONS.find((option) => option.value === weekday)?.label ||
  WEEKDAY_OPTIONS.find((option) => option.value === 0)!.label;

const normalizeNumeric = (value: string): number | undefined => {
  if (value.trim() === '') {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const normalizeLabels = (labels: string[]) => labels.map((label) => label.trim()).filter(Boolean);

const getPlannerErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof ApiError) {
    const separatorIndex = error.message.indexOf(': ');
    const payload = separatorIndex >= 0 ? error.message.slice(separatorIndex + 2) : '';

    if (payload) {
      try {
        const parsed = JSON.parse(payload) as { error?: string; message?: string };
        if (parsed.error) {
          return parsed.error;
        }
        if (parsed.message) {
          return parsed.message;
        }
      } catch {
        // Fall through to generic handling when the backend response is not JSON.
      }
    }

    if (error.status >= 500) {
      return fallback;
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
};

export const ProgramBuilderV2: React.FC<ProgramBuilderV2Props> = ({
  program,
  onProgramUpdate,
  onClose,
}) => {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [exerciseLibrary, setExerciseLibrary] = useState<Exercise[]>([]);
  const [filteredExerciseLibrary, setFilteredExerciseLibrary] = useState<Exercise[]>([]);
  const [muscleGroups, setMuscleGroups] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMuscleGroup, setSelectedMuscleGroup] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const [originalDays, setOriginalDays] = useState<PlannerDay[]>([]);
  const [draftDays, setDraftDays] = useState<PlannerDay[]>([]);
  const [selectedDayNumber, setSelectedDayNumber] = useState<number>(1);

  const [periodizationOverview, setPeriodizationOverview] = useState<ProgramPeriodizationOverview | null>(null);
  const [periodizationLoading, setPeriodizationLoading] = useState(false);
  const [periodizationActionLoading, setPeriodizationActionLoading] = useState(false);

  const [daySettingsOpen, setDaySettingsOpen] = useState(false);
  const [exercisePickerOpen, setExercisePickerOpen] = useState(false);
  const [variationPickerOpen, setVariationPickerOpen] = useState(false);
  const [variationOptions, setVariationOptions] = useState<Exercise[]>([]);
  const [variationSource, setVariationSource] = useState<Exercise | null>(null);
  const [variationTargetDay, setVariationTargetDay] = useState<number>(1);
  const [exerciseEditor, setExerciseEditor] = useState<ExerciseEditorState | null>(null);
  const [exerciseEditorTargetDayNumber, setExerciseEditorTargetDayNumber] = useState<number | null>(null);
  const [rotationEditor, setRotationEditor] = useState<RotationEditorState | null>(null);
  const [rotationFilter, setRotationFilter] = useState<number | null>(null);

  useEffect(() => {
    void loadInitialData();
  }, [program.id]);

  useEffect(() => {
    let nextExercises = [...exerciseLibrary];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      nextExercises = nextExercises.filter((exercise) =>
        exercise.name.toLowerCase().includes(query) ||
        exercise.description?.toLowerCase().includes(query)
      );
    }

    if (selectedMuscleGroup) {
      nextExercises = nextExercises.filter((exercise) => exercise.muscleGroup === selectedMuscleGroup);
    }

    if (selectedCategory) {
      nextExercises = nextExercises.filter((exercise) => exercise.category === selectedCategory);
    }

    setFilteredExerciseLibrary(nextExercises);
  }, [exerciseLibrary, searchQuery, selectedMuscleGroup, selectedCategory]);

  useEffect(() => {
    setRotationFilter(null);
  }, [selectedDayNumber]);

  const hasUnsavedChanges =
    JSON.stringify(buildSnapshot(draftDays)) !== JSON.stringify(buildSnapshot(originalDays));

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedChanges) {
        return;
      }

      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      setPeriodizationLoading(true);

      const [library, muscleGroupOptions, categoryOptions, programExercises, rotations] = await Promise.all([
        ExerciseService.getAll(),
        ExerciseService.getMuscleGroups(),
        ExerciseService.getCategories(),
        ProgramExerciseService.getProgramExercises(program.id),
        RotationalTrainingService.getAll(program.id),
      ]);

      const rotationMap: Record<number, RotationalConfig> = {};
      rotations.rotations.forEach((rotation) => {
        rotationMap[rotation.dayNumber] = rotation;
      });

      const nextDays = buildDaysFromResponse({
        exercisesByDay: programExercises.exercisesByDay,
        dayNames: programExercises.dayNames,
        dayWeekdays: programExercises.dayWeekdays,
        rotationsByDay: rotationMap,
      });

      setExerciseLibrary(library);
      setFilteredExerciseLibrary(library);
      setMuscleGroups(muscleGroupOptions);
      setCategories(categoryOptions);
      setOriginalDays(cloneDays(nextDays));
      setDraftDays(cloneDays(nextDays));
      setSelectedDayNumber(nextDays[0]?.dayNumber || 1);

      try {
        const overview = await ProgramPeriodizationService.getOverview(program.id);
        setPeriodizationOverview(overview);
      } catch {
        setPeriodizationOverview(null);
      }
    } catch (loadError) {
      console.error(loadError);
      setError('Неуспешно зареждане на програмния редактор.');
    } finally {
      setLoading(false);
      setPeriodizationLoading(false);
    }
  };

  const selectedDay = draftDays.find((day) => day.dayNumber === selectedDayNumber) || draftDays[0] || null;
  const exerciseBeingEdited = exerciseEditor
    ? draftDays
        .find((day) => day.dayNumber === exerciseEditor.dayNumber)
        ?.exercises.find((exercise) => exercise.clientKey === exerciseEditor.exerciseKey)
    : null;
  const exerciseEditorDestinationDay =
    draftDays.find((day) => day.dayNumber === (exerciseEditorTargetDayNumber ?? exerciseEditor?.dayNumber ?? selectedDayNumber)) || null;

  const setSelectedDayPatch = (patch: Partial<PlannerDay>) => {
    if (!selectedDay) {
      return;
    }

    setDraftDays((currentDays) =>
      currentDays.map((day) =>
        day.dayNumber === selectedDay.dayNumber
          ? {
              ...day,
              ...patch,
            }
          : day
      )
    );
  };

  const showSuccess = (message: string) => {
    setSuccess(message);
    window.setTimeout(() => setSuccess(null), 3000);
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const handleCloseBuilder = () => {
    if (hasUnsavedChanges && !window.confirm('Има незапазени промени. Сигурни ли сте, че искате да излезете?')) {
      return;
    }

    onClose?.();
  };

  const handleDiscard = () => {
    if (!window.confirm('Да изхвърля ли локалните промени?')) {
      return;
    }

    const resetDays = cloneDays(originalDays);
    setDraftDays(resetDays);
    setSelectedDayNumber(resetDays[0]?.dayNumber || 1);
    clearMessages();
  };

  const handleAddDay = () => {
    const usedDayNumbers = new Set(draftDays.map((day) => day.dayNumber));
    const nextDayNumber = [1, 2, 3, 4, 5, 6, 7].find((dayNumber) => !usedDayNumbers.has(dayNumber));

    if (!nextDayNumber) {
      return;
    }

    const nextDay: PlannerDay = {
      dayNumber: nextDayNumber,
      name: `Day ${nextDayNumber}`,
      weekday: null,
      exercises: [],
      rotationConfig: null,
    };

    setDraftDays((currentDays) => [...currentDays, nextDay].sort((a, b) => a.dayNumber - b.dayNumber));
    setSelectedDayNumber(nextDayNumber);
    setDaySettingsOpen(true);
  };

  const handleRemoveSelectedDay = () => {
    if (!selectedDay) {
      return;
    }

    if (draftDays.length === 1) {
      setError('Програмата трябва да има поне един ден.');
      return;
    }

    if (!window.confirm(`Да премахна ${selectedDay.name}?`)) {
      return;
    }

    const nextDays = draftDays.filter((day) => day.dayNumber !== selectedDay.dayNumber);
    setDraftDays(nextDays);
    setSelectedDayNumber(nextDays[0]?.dayNumber || 1);
    setDaySettingsOpen(false);
  };

  const handleMoveExercise = (exerciseKey: string, direction: -1 | 1) => {
    if (!selectedDay) {
      return;
    }

    const index = selectedDay.exercises.findIndex((exercise) => exercise.clientKey === exerciseKey);
    const nextIndex = index + direction;

    if (index < 0 || nextIndex < 0 || nextIndex >= selectedDay.exercises.length) {
      return;
    }

    const nextExercises = selectedDay.exercises.map((exercise) => ({ ...exercise }));
    [nextExercises[index], nextExercises[nextIndex]] = [nextExercises[nextIndex], nextExercises[index]];
    nextExercises.forEach((exercise, nextOrderIndex) => {
      exercise.orderInSession = nextOrderIndex + 1;
    });

    setSelectedDayPatch({ exercises: normalizeSupersetGroups(nextExercises) });
  };

  const handleDeleteExercise = (exerciseKey: string) => {
    if (!selectedDay) {
      return;
    }

    const nextExercises = normalizeSupersetGroups(
      selectedDay.exercises
        .filter((exercise) => exercise.clientKey !== exerciseKey)
        .map((exercise, index) => ({
          ...exercise,
          orderInSession: index + 1,
        }))
    );

    setSelectedDayPatch({ exercises: nextExercises });
  };

  const handleCreateSuperset = (exerciseKey: string) => {
    if (!selectedDay) {
      return;
    }

    const index = selectedDay.exercises.findIndex((exercise) => exercise.clientKey === exerciseKey);
    if (index < 0 || index === selectedDay.exercises.length - 1) {
      setError('Суперсерия може да се създаде само с упражнение под текущото.');
      return;
    }

    const current = selectedDay.exercises[index];
    const next = selectedDay.exercises[index + 1];
    if (current.supersetGroup || next.supersetGroup) {
      setError('Поне едно от упражненията вече участва в суперсерия.');
      return;
    }

    const supersetGroup = `superset-${Date.now()}`;
    setSelectedDayPatch({
      exercises: selectedDay.exercises.map((exercise) =>
        exercise.clientKey === current.clientKey || exercise.clientKey === next.clientKey
          ? { ...exercise, supersetGroup }
          : exercise
      ),
    });
  };

  const handleRemoveSuperset = (supersetGroup: string) => {
    if (!selectedDay) {
      return;
    }

    setSelectedDayPatch({
      exercises: selectedDay.exercises.map((exercise) =>
        exercise.supersetGroup === supersetGroup
          ? { ...exercise, supersetGroup: null }
          : exercise
      ),
    });
  };

  const handleOpenExerciseEditor = (exerciseKey: string) => {
    if (!selectedDay) {
      return;
    }

    setExerciseEditorTargetDayNumber(selectedDay.dayNumber);
    setExerciseEditor({
      dayNumber: selectedDay.dayNumber,
      exerciseKey,
    });
  };

  const handleSaveExerciseEditor = (formData: FormData) => {
    if (!exerciseBeingEdited || !selectedDay) {
      return;
    }

    const destinationDayNumber = Number(formData.get('dayNumber'));
    const destinationDay = draftDays.find((day) => day.dayNumber === destinationDayNumber) || null;
    const rawRotationIndexes = formData.getAll('rotationIndexes');
    const resolvedRotationIndexes: number[] | null =
      !destinationDay?.rotationConfig || rawRotationIndexes.length === 0
        ? null
        : rawRotationIndexes.map((v) => Number(v));
    const nextExercise: PlannerExercise = {
      ...exerciseBeingEdited,
      targetSets: normalizeNumeric(String(formData.get('targetSets') || '')),
      targetReps: normalizeNumeric(String(formData.get('targetReps') || '')),
      targetWeight: normalizeNumeric(String(formData.get('targetWeight') || '')),
      targetDuration: normalizeNumeric(String(formData.get('targetDuration') || '')),
      targetDistance: normalizeNumeric(String(formData.get('targetDistance') || '')),
      notes: String(formData.get('notes') || '').trim() || undefined,
      rotationIndexes: resolvedRotationIndexes,
      isFocused: formData.get('isFocused') !== null,
    };

    setDraftDays((currentDays) => {
      const sourceDay = currentDays.find((day) => day.dayNumber === exerciseEditor?.dayNumber);
      const destinationDay = currentDays.find((day) => day.dayNumber === destinationDayNumber);

      if (!sourceDay || !destinationDay) {
        return currentDays;
      }

      if (sourceDay.dayNumber === destinationDay.dayNumber) {
        return currentDays.map((day) =>
          day.dayNumber === sourceDay.dayNumber
            ? {
                ...day,
                exercises: day.exercises.map((exercise) =>
                  exercise.clientKey === nextExercise.clientKey ? nextExercise : exercise
                ),
              }
            : day
        );
      }

      return currentDays.map((day) => {
        if (day.dayNumber === sourceDay.dayNumber) {
          return {
            ...day,
            exercises: day.exercises
              .filter((exercise) => exercise.clientKey !== nextExercise.clientKey)
              .map((exercise, index) => ({
                ...exercise,
                orderInSession: index + 1,
              })),
          };
        }

        if (day.dayNumber === destinationDay.dayNumber) {
          const movedExercise = {
            ...nextExercise,
            dayOfWeek: destinationDay.dayNumber,
            orderInSession: day.exercises.length + 1,
          };

          return {
            ...day,
            exercises: [...day.exercises, movedExercise].map((exercise, index) => ({
              ...exercise,
              orderInSession: index + 1,
            })),
          };
        }

        return day;
      });
    });

    setExerciseEditor(null);
    setExerciseEditorTargetDayNumber(null);
  };

  const handleOpenRotationEditor = () => {
    if (!selectedDay) {
      return;
    }

    setRotationEditor({
      dayNumber: selectedDay.dayNumber,
      rotationName: selectedDay.rotationConfig?.rotationName || '',
      rotationLabels: selectedDay.rotationConfig?.rotationLabels.length
        ? [...selectedDay.rotationConfig.rotationLabels]
        : ['', ''],
    });
  };

  const handleSaveRotationEditor = () => {
    if (!rotationEditor) {
      return;
    }

    const labels = normalizeLabels(rotationEditor.rotationLabels);
    if (!rotationEditor.rotationName.trim()) {
      setError('Името на ротацията е задължително.');
      return;
    }

    if (labels.length < 2) {
      setError('Необходими са поне две rotation labels.');
      return;
    }

    setDraftDays((currentDays) =>
      currentDays.map((day) => {
        if (day.dayNumber !== rotationEditor.dayNumber) {
          return day;
        }

        const currentIndex = day.rotationConfig?.currentIndex ?? 0;
        return {
          ...day,
          rotationConfig: {
            ...(day.rotationConfig || {
              id: 0,
              programId: program.id,
              dayNumber: day.dayNumber,
              currentIndex: Math.min(currentIndex, labels.length - 1),
              currentLabel: labels[Math.min(currentIndex, labels.length - 1)],
              rotationCount: labels.length,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            }),
            rotationName: rotationEditor.rotationName.trim(),
            rotationLabels: labels,
            currentIndex: Math.min(currentIndex, labels.length - 1),
            currentLabel: labels[Math.min(currentIndex, labels.length - 1)],
            rotationCount: labels.length,
          },
          exercises: day.exercises.map((exercise) => {
            if (exercise.rotationIndexes == null) {
              return exercise;
            }

            const filtered = exercise.rotationIndexes.filter((idx) => idx < labels.length);
            return {
              ...exercise,
              rotationIndexes: filtered.length > 0 ? filtered : null,
            };
          }),
        };
      })
    );

    setRotationEditor(null);
  };

  const handleRemoveRotation = () => {
    if (!selectedDay) {
      return;
    }

    if (!window.confirm('Да премахна ли ротацията за този ден?')) {
      return;
    }

    setSelectedDayPatch({
      rotationConfig: null,
      exercises: selectedDay.exercises.map((exercise) => ({
        ...exercise,
        rotationIndexes: null,
      })),
    });
    setRotationEditor(null);
  };

  const appendExerciseToDay = (exercise: Exercise, dayNumber: number) => {
    const targetDay = draftDays.find((day) => day.dayNumber === dayNumber);
    if (!targetDay) {
      return;
    }

    const nextExercise: PlannerExercise = {
      id: -1,
      clientKey: createClientKey(exercise.id),
      exercise,
      dayOfWeek: dayNumber,
      orderInSession: targetDay.exercises.length + 1,
      targetSets: undefined,
      targetReps: undefined,
      targetWeight: undefined,
      targetDuration: undefined,
      targetDistance: undefined,
      notes: undefined,
      supersetGroup: undefined,
      rotationIndexes: targetDay.rotationConfig ? null : undefined,
      isFocused: false,
      createdAt: new Date().toISOString(),
      updatedAt: undefined,
      isNew: true,
    };

    setDraftDays((currentDays) =>
      currentDays.map((day) =>
        day.dayNumber === dayNumber
          ? { ...day, exercises: [...day.exercises, nextExercise] }
          : day
      )
    );
    setExercisePickerOpen(false);
    setVariationPickerOpen(false);
    setVariationOptions([]);
    setVariationSource(null);
  };

  const handleAddExercise = async (exercise: Exercise, dayNumber: number) => {
    if (exercise.hasVariations) {
      setVariationSource(exercise);
      setVariationTargetDay(dayNumber);
      setVariationPickerOpen(true);
      try {
        const variations = await ExerciseService.getVariations(exercise.id);
        setVariationOptions([variations.baseExercise, ...variations.variations]);
      } catch (variationError) {
        console.error(variationError);
        setVariationOptions([exercise]);
      }
      return;
    }

    appendExerciseToDay(exercise, dayNumber);
  };

  const handleSave = async () => {
    try {
      clearMessages();
      setSaving(true);

      const emptyNameDay = draftDays.find((day) => day.name.trim() === '');
      if (emptyNameDay) {
        throw new Error(`Ден ${emptyNameDay.dayNumber} няма име.`);
      }

      const weekdayAssignments = new Map<number, number>();
      for (const day of draftDays) {
        if (day.weekday == null) {
          continue;
        }

        const existingDay = weekdayAssignments.get(day.weekday);
        if (existingDay != null) {
          throw new Error(`Дни ${existingDay} и ${day.dayNumber} използват един и същи weekday.`);
        }

        weekdayAssignments.set(day.weekday, day.dayNumber);
      }

      const normalizedDraftDays = draftDays.map((day) => ({
        ...day,
        exercises: normalizeSupersetGroups(day.exercises),
      }));
      const originalDayMap = new Map(originalDays.map((day) => [day.dayNumber, day]));
      const draftDayMap = new Map(normalizedDraftDays.map((day) => [day.dayNumber, day]));
      const originalExercisesById = new Map<number, { dayNumber: number; exercise: PlannerExercise }>();
      const deletedExerciseIds = new Set<number>();

      originalDays.forEach((day) => {
        day.exercises.forEach((exercise) => {
          originalExercisesById.set(exercise.id, { dayNumber: day.dayNumber, exercise });
        });
      });

      for (const originalDay of originalDays) {
        const draftDay = draftDayMap.get(originalDay.dayNumber);

        if (!draftDay) {
          for (const exercise of originalDay.exercises) {
            await ProgramExerciseService.removeExerciseFromProgram(program.id, exercise.id);
            deletedExerciseIds.add(exercise.id);
          }

          if (originalDay.rotationConfig) {
            await RotationalTrainingService.delete(program.id, originalDay.dayNumber);
          }

          if (
            originalDay.name !== `Day ${originalDay.dayNumber}` ||
            originalDay.weekday !== null
          ) {
            await ProgramDayService.deleteDayName(program.id, originalDay.dayNumber);
          }
        }
      }

      for (const draftDay of normalizedDraftDays) {
        const originalDay = originalDayMap.get(draftDay.dayNumber);
        const dayNameChanged = !originalDay || originalDay.name !== draftDay.name;
        const weekdayChanged = !originalDay || originalDay.weekday !== draftDay.weekday;

        if (dayNameChanged || weekdayChanged) {
          await ProgramDayService.updateDay(program.id, draftDay.dayNumber, {
            name: draftDay.name.trim(),
            weekday: draftDay.weekday,
          });
        }

        const originalRotation = originalDay?.rotationConfig || null;
        const nextRotation = draftDay.rotationConfig;
        const rotationChanged =
          JSON.stringify(
            originalRotation
              ? {
                  rotationName: originalRotation.rotationName,
                  rotationLabels: originalRotation.rotationLabels,
                }
              : null
          ) !==
          JSON.stringify(
            nextRotation
              ? {
                  rotationName: nextRotation.rotationName,
                  rotationLabels: nextRotation.rotationLabels,
                }
              : null
          );

        if (rotationChanged) {
          if (nextRotation) {
            const response = await RotationalTrainingService.save(program.id, draftDay.dayNumber, {
              rotationName: nextRotation.rotationName,
              rotationLabels: nextRotation.rotationLabels,
            });

            draftDay.rotationConfig = response.config;
          } else if (originalRotation) {
            await RotationalTrainingService.delete(program.id, draftDay.dayNumber);
          }
        }
      }

      originalExercisesById.forEach(({ exercise }) => {
        const stillExists = draftDays.some((day) =>
          day.exercises.some((draftExercise) => !draftExercise.isNew && draftExercise.id === exercise.id)
        );

        if (!stillExists) {
          deletedExerciseIds.add(exercise.id);
        }
      });

      for (const deletedExerciseId of Array.from(deletedExerciseIds)) {
        await ProgramExerciseService.removeExerciseFromProgram(program.id, deletedExerciseId);
      }

      const clientKeyToPersistedId = new Map<string, number>();

      for (const draftDay of normalizedDraftDays) {
        for (const [index, exercise] of Array.from(draftDay.exercises.entries())) {
          const targetOrder = index + 1;

          if (exercise.isNew) {
            const response = await ProgramExerciseService.addExerciseToProgram(program.id, {
              exerciseId: exercise.exercise.id,
              dayOfWeek: draftDay.dayNumber,
              targetSets: exercise.targetSets,
              targetReps: exercise.targetReps,
              targetWeight: exercise.targetWeight,
              targetDuration: exercise.targetDuration,
              targetDistance: exercise.targetDistance,
              notes: exercise.notes,
              supersetGroup: exercise.supersetGroup,
              rotationIndexes: exercise.rotationIndexes ?? null,
              isFocused: exercise.isFocused ?? false,
            });
            clientKeyToPersistedId.set(exercise.clientKey, response.id);
            continue;
          }

          const originalRecord = originalExercisesById.get(exercise.id);
          if (!originalRecord) {
            continue;
          }

          const originalExercise = originalRecord.exercise;
          const changed =
            originalRecord.dayNumber !== draftDay.dayNumber ||
            originalExercise.orderInSession !== targetOrder ||
            originalExercise.targetSets !== exercise.targetSets ||
            originalExercise.targetReps !== exercise.targetReps ||
            originalExercise.targetWeight !== exercise.targetWeight ||
            originalExercise.targetDuration !== exercise.targetDuration ||
            originalExercise.targetDistance !== exercise.targetDistance ||
            (originalExercise.notes || '') !== (exercise.notes || '') ||
            (originalExercise.supersetGroup || null) !== (exercise.supersetGroup || null) ||
            JSON.stringify(originalExercise.rotationIndexes ?? null) !== JSON.stringify(exercise.rotationIndexes ?? null) ||
            !!originalExercise.isFocused !== !!exercise.isFocused;

          if (!changed) {
            clientKeyToPersistedId.set(exercise.clientKey, exercise.id);
            continue;
          }

          await ProgramExerciseService.updateProgramExercise(program.id, exercise.id, {
            dayOfWeek: draftDay.dayNumber,
            orderInSession: targetOrder,
            targetSets: exercise.targetSets,
            targetReps: exercise.targetReps,
            targetWeight: exercise.targetWeight,
            targetDuration: exercise.targetDuration,
            targetDistance: exercise.targetDistance,
            notes: exercise.notes,
            supersetGroup: exercise.supersetGroup ?? null,
            rotationIndexes: exercise.rotationIndexes ?? null,
            isFocused: exercise.isFocused ?? false,
          });
          clientKeyToPersistedId.set(exercise.clientKey, exercise.id);
        }
      }

      for (const draftDay of normalizedDraftDays) {
        const orders = draftDay.exercises
          .map((exercise, index) => ({
            exerciseId: clientKeyToPersistedId.get(exercise.clientKey) || exercise.id,
            newOrder: index + 1,
          }))
          .filter((item) => item.exerciseId > 0);

        if (orders.length > 1) {
          await ProgramExerciseService.reorderExercisesInDay(program.id, draftDay.dayNumber, orders);
        }
      }

      const persistedDays = normalizedDraftDays.map((day) => ({
        ...day,
        exercises: day.exercises.map((exercise, index) => ({
          ...exercise,
          id: clientKeyToPersistedId.get(exercise.clientKey) || exercise.id,
          clientKey:
            clientKeyToPersistedId.get(exercise.clientKey) != null
              ? `saved-${clientKeyToPersistedId.get(exercise.clientKey)}`
              : exercise.clientKey,
          isNew: false,
          dayOfWeek: day.dayNumber,
          orderInSession: index + 1,
        })),
      }));

      setDraftDays(cloneDays(persistedDays));
      setOriginalDays(cloneDays(persistedDays));
      showSuccess('Промените по програмата са запазени.');
      window.dispatchEvent(new CustomEvent(PROGRAM_EXERCISES_UPDATED_EVENT));
      onProgramUpdate?.();
    } catch (saveError) {
      console.error(saveError);
      setError(getPlannerErrorMessage(saveError, 'Неуспешно записване. Локалните промени остават налични.'));
    } finally {
      setSaving(false);
    }
  };

  const handleEnablePeriodization = async () => {
    try {
      setPeriodizationActionLoading(true);
      const response = await ProgramPeriodizationService.enable(program.id, {
        periodType: 'stabilization_endurance',
        targetWeeks: 4,
      });
      setPeriodizationOverview((current) => ({
        ...current,
        programId: program.id,
        periodizationEnabled: response.periodizationEnabled,
        currentPeriodType: response.currentPeriodType,
        activeBlock: response.activeBlock,
        timeline: current?.timeline || [],
        transitions: current?.transitions || [],
        recommendation: current?.recommendation || {
          recommended: false,
          reason: '',
          suggestedPeriodType: response.currentPeriodType || 'stabilization_endurance',
          action: 'stay',
        },
      }));
      showSuccess('Периодизацията е активирана.');
    } catch (periodizationError) {
      console.error(periodizationError);
      setError('Неуспешно активиране на periodization overview.');
    } finally {
      setPeriodizationActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Paper sx={{ p: 4, minHeight: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Stack spacing={2} alignItems="center">
          <CircularProgress />
          <Typography>Зареждане на програмния редактор...</Typography>
        </Stack>
      </Paper>
    );
  }

  return (
    <Box>
      <Box className="ppv-screen-content" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" color="text.secondary">
                Program Planner
              </Typography>
              <Typography variant="h5" sx={{ wordBreak: 'break-word' }}>
                {program.name}
              </Typography>
            </Box>

            {onClose ? (
              <IconButton onClick={handleCloseBuilder} aria-label="Изход от редактора">
                <ArrowBackIcon />
              </IconButton>
            ) : null}
          </Stack>

          {error ? <Alert severity="error" onClose={() => setError(null)}>{error}</Alert> : null}
          {success ? <Alert severity="success" onClose={() => setSuccess(null)}>{success}</Alert> : null}

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
          >
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                icon={<WarningAmberIcon />}
                label={hasUnsavedChanges ? 'Има незапазени промени' : 'Всичко е запазено'}
                color={hasUnsavedChanges ? 'warning' : 'success'}
                variant={hasUnsavedChanges ? 'filled' : 'outlined'}
              />
              <Chip
                label={`${draftDays.length} дни`}
                variant="outlined"
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={draftDays.length >= 7}
                onClick={handleAddDay}
              >
                Добави ден
              </Button>
              <Button
                variant="outlined"
                startIcon={<PrintIcon />}
                onClick={() => window.print()}
                className="no-print"
              >
                Принтирай
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                disabled={!hasUnsavedChanges || saving}
                onClick={handleDiscard}
              >
                Откажи промени
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
                disabled={!hasUnsavedChanges || saving}
                onClick={handleSave}
              >
                {saving ? 'Запазване...' : 'Запази'}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      <Accordion disableGutters sx={{ borderRadius: 3, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center', pr: 1 }}>
            <Box>
              <Typography variant="subtitle1">Periodization overview</Typography>
              <Typography variant="body2" color="text.secondary">
                {periodizationOverview?.currentPeriodType || 'Няма активен период'}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              disabled={periodizationActionLoading || periodizationOverview?.periodizationEnabled}
              onClick={(event) => {
                event.stopPropagation();
                void handleEnablePeriodization();
              }}
            >
              {periodizationOverview?.periodizationEnabled ? 'Активна' : 'Активирай'}
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {periodizationLoading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2">Зареждане...</Typography>
            </Stack>
          ) : periodizationOverview ? (
            <Stack spacing={1}>
              <Typography variant="body2">
                Текущ блок: {periodizationOverview.activeBlock?.periodType || 'Няма'}
              </Typography>
              {periodizationOverview.recommendation?.reason ? (
                <Alert severity={periodizationOverview.recommendation.recommended ? 'info' : 'success'}>
                  {periodizationOverview.recommendation.reason}
                </Alert>
              ) : null}
            </Stack>
          ) : (
            <Alert severity="info">Periodization overview не е наличен, но редакторът остава използваем.</Alert>
          )}
        </AccordionDetails>
      </Accordion>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: '320px minmax(0, 1fr)' } }}>
        <Paper sx={{ p: 2, borderRadius: 3 }}>
          <Stack spacing={1.5}>
            <Typography variant="subtitle1">Дни</Typography>
            {draftDays
              .sort((a, b) => a.dayNumber - b.dayNumber)
              .map((day) => {
                const selected = day.dayNumber === selectedDayNumber;
                return (
                  <Card
                    key={day.dayNumber}
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      borderColor: selected ? 'primary.main' : 'divider',
                      bgcolor: selected ? 'primary.50' : 'background.paper',
                    }}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Stack spacing={1}>
                        <Button
                          onClick={() => setSelectedDayNumber(day.dayNumber)}
                          sx={{ justifyContent: 'space-between', textAlign: 'left', px: 0, py: 0, minHeight: 0 }}
                        >
                          <Box>
                            <Typography variant="subtitle2">{day.name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatWeekday(day.weekday)}
                            </Typography>
                          </Box>
                          <Chip size="small" label={`${day.exercises.length} упражнения`} />
                        </Button>

                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {day.rotationConfig ? (
                            <Chip
                              size="small"
                              color="secondary"
                              variant={selected ? 'filled' : 'outlined'}
                              label={`${day.rotationConfig.rotationName}: ${day.rotationConfig.currentLabel}`}
                            />
                          ) : null}
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
          </Stack>
        </Paper>

        {selectedDay ? (
          <Stack spacing={2}>
            <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
              <Stack spacing={2}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                >
                  <Box>
                    <Typography variant="h6">{selectedDay.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatWeekday(selectedDay.weekday)}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button variant="outlined" startIcon={<SettingsIcon />} onClick={() => setDaySettingsOpen(true)}>
                      Ден
                    </Button>
                    <Button variant="contained" startIcon={<FitnessCenterIcon />} onClick={() => setExercisePickerOpen(true)}>
                      Добави упражнение
                    </Button>
                  </Stack>
                </Stack>

                <MuscleLoadVisualization
                  programDays={Object.fromEntries(
                    draftDays.map((day) => [day.dayNumber, day.exercises])
                  )}
                  selectedDay={selectedDay.dayNumber}
                  onDayChange={(dayNumber) => {
                    if (dayNumber != null) {
                      setSelectedDayNumber(dayNumber);
                    }
                  }}
                />

                <Divider />

                {selectedDay.rotationConfig && selectedDay.rotationConfig.rotationLabels.length > 1 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip
                      label="Всички"
                      variant={rotationFilter === null ? 'filled' : 'outlined'}
                      onClick={() => setRotationFilter(null)}
                    />
                    {selectedDay.rotationConfig.rotationLabels.map((label, idx) => (
                      <Chip
                        key={idx}
                        label={label}
                        variant={rotationFilter === idx ? 'filled' : 'outlined'}
                        onClick={() => setRotationFilter(idx)}
                        sx={rotationFilter === idx
                          ? { bgcolor: ROTATION_COLORS[idx % ROTATION_COLORS.length], color: 'common.white' }
                          : { borderColor: ROTATION_COLORS[idx % ROTATION_COLORS.length], color: ROTATION_COLORS[idx % ROTATION_COLORS.length] }
                        }
                      />
                    ))}
                  </Stack>
                )}

                {(() => {
                  const filteredExercises = rotationFilter !== null
                    ? selectedDay.exercises.filter(
                        (ex) => ex.rotationIndexes == null || ex.rotationIndexes.includes(rotationFilter)
                      )
                    : selectedDay.exercises;

                  if (filteredExercises.length === 0) {
                    return <Alert severity="info">Все още няма упражнения за този ден.</Alert>;
                  }

                  return (
                  <Stack spacing={1.5}>
                    {filteredExercises.map((exercise, filteredIndex) => {
                      const realIndex = selectedDay.exercises.indexOf(exercise);
                      const canMoveUp = realIndex > 0;
                      const canMoveDown = realIndex < selectedDay.exercises.length - 1;
                      const isSupersetStart =
                        !!exercise.supersetGroup &&
                        filteredExercises[filteredIndex - 1]?.supersetGroup !== exercise.supersetGroup;
                      const canCreateSuperset =
                        realIndex < selectedDay.exercises.length - 1 &&
                        !exercise.supersetGroup &&
                        !selectedDay.exercises[realIndex + 1]?.supersetGroup;
                      const rotationBadges: { label: string; colorIndex: number }[] =
                        selectedDay.rotationConfig && exercise.rotationIndexes != null
                          ? exercise.rotationIndexes.map((idx) => ({
                              label: selectedDay.rotationConfig!.rotationLabels[idx] || `Rotation ${idx + 1}`,
                              colorIndex: idx,
                            }))
                          : [];

                      return (
                        <Card
                          key={exercise.clientKey}
                          variant="outlined"
                          sx={{
                            borderRadius: 3,
                            borderLeft: 4,
                            borderLeftColor: exercise.supersetGroup ? 'warning.main' : 'divider',
                          }}
                        >
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            <Stack spacing={1.5}>
                              <Stack direction="row" justifyContent="space-between" spacing={1}>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="subtitle1" sx={{ wordBreak: 'break-word' }}>
                                    {filteredIndex + 1}. {exercise.exercise.variationName
                                      ? `${exercise.exercise.variationName} ${exercise.exercise.parentExerciseName || exercise.exercise.name}`
                                      : exercise.exercise.name}
                                  </Typography>
                                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                    <Chip size="small" label={`Серии ${exercise.targetSets ?? '-'}`} />
                                    <Chip size="small" label={`Повт. ${exercise.targetReps ?? '-'}`} />
                                    <Chip size="small" label={`Тежест ${exercise.targetWeight ?? '-'}`} />
                                    {exercise.targetDuration != null ? <Chip size="small" label={`Време ${exercise.targetDuration}`} /> : null}
                                    {exercise.targetDistance != null ? <Chip size="small" label={`Дистанция ${exercise.targetDistance}`} /> : null}
                                    {exercise.notes ? <Chip size="small" variant="outlined" label="Бележка" /> : null}
                                    {exercise.isFocused ? <Chip size="small" color="secondary" label="Focus" /> : null}
                                    {rotationBadges.map((badge) => (
                                      <Chip
                                        key={badge.colorIndex}
                                        size="small"
                                        label={badge.label}
                                        sx={{
                                          bgcolor: ROTATION_COLORS[badge.colorIndex % ROTATION_COLORS.length],
                                          color: 'common.white',
                                        }}
                                      />
                                    ))}
                                    {selectedDay.rotationConfig && exercise.rotationIndexes == null ? (
                                      <Chip size="small" variant="outlined" label="Всички rotation labels" />
                                    ) : null}
                                    {exercise.supersetGroup ? <Chip size="small" color="warning" label="Superset" /> : null}
                                  </Stack>
                                </Box>

                                <Stack direction="row" spacing={0.5} alignItems="flex-start">
                                  <IconButton
                                    onClick={() => handleMoveExercise(exercise.clientKey, -1)}
                                    disabled={!canMoveUp}
                                    aria-label="Премести нагоре"
                                  >
                                    <ArrowUpIcon />
                                  </IconButton>
                                  <IconButton
                                    onClick={() => handleMoveExercise(exercise.clientKey, 1)}
                                    disabled={!canMoveDown}
                                    aria-label="Премести надолу"
                                  >
                                    <ArrowDownIcon />
                                  </IconButton>
                                  <IconButton onClick={() => handleOpenExerciseEditor(exercise.clientKey)} aria-label="Редактирай упражнение">
                                    <EditIcon />
                                  </IconButton>
                                  <IconButton onClick={() => handleDeleteExercise(exercise.clientKey)} color="error" aria-label="Изтрий упражнение">
                                    <DeleteIcon />
                                  </IconButton>
                                </Stack>
                              </Stack>

                              {exercise.notes ? (
                                <Typography variant="body2" color="text.secondary">
                                  {exercise.notes}
                                </Typography>
                              ) : null}

                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                {canCreateSuperset ? (
                                  <Button
                                    size="small"
                                    startIcon={<ShuffleIcon />}
                                    onClick={() => handleCreateSuperset(exercise.clientKey)}
                                  >
                                    Superset с долното
                                  </Button>
                                ) : null}
                                {isSupersetStart && exercise.supersetGroup ? (
                                  <Button
                                    size="small"
                                    color="warning"
                                    onClick={() => handleRemoveSuperset(exercise.supersetGroup!)}
                                  >
                                    Премахни superset
                                  </Button>
                                ) : null}
                              </Stack>
                            </Stack>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Stack>
                  );
                })()}
              </Stack>
            </Paper>
          </Stack>
        ) : null}
      </Box>

      <Dialog open={daySettingsOpen} onClose={() => setDaySettingsOpen(false)} fullScreen={fullScreenDialog} fullWidth maxWidth="sm">
        <DialogTitle>Настройки на деня</DialogTitle>
        <DialogContent>
          {selectedDay ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Име на деня"
                value={selectedDay.name}
                onChange={(event) => setSelectedDayPatch({ name: event.target.value })}
                fullWidth
              />

              <FormControl fullWidth>
                <InputLabel>Weekday</InputLabel>
                <Select
                  label="Weekday"
                  value={selectedDay.weekday ?? 0}
                  onChange={(event) =>
                    setSelectedDayPatch({
                      weekday: Number(event.target.value) === 0 ? null : Number(event.target.value),
                    })
                  }
                >
                  {WEEKDAY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Button variant="outlined" startIcon={<ShuffleIcon />} onClick={handleOpenRotationEditor}>
                  {selectedDay.rotationConfig ? 'Редактирай rotation' : 'Добави rotation'}
                </Button>
                {selectedDay.rotationConfig ? (
                  <Button variant="outlined" color="warning" onClick={handleRemoveRotation}>
                    Премахни rotation
                  </Button>
                ) : null}
                <Button
                  variant="outlined"
                  color="error"
                  disabled={draftDays.length === 1}
                  onClick={handleRemoveSelectedDay}
                >
                  Премахни деня
                </Button>
              </Stack>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDaySettingsOpen(false)}>Готово</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={exercisePickerOpen} onClose={() => setExercisePickerOpen(false)} fullScreen={fullScreenDialog} fullWidth maxWidth="md">
        <DialogTitle>Exercise Library</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Търси упражнение"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <FormControl fullWidth>
                <InputLabel>Muscle</InputLabel>
                <Select
                  label="Muscle"
                  value={selectedMuscleGroup}
                  onChange={(event) => setSelectedMuscleGroup(String(event.target.value))}
                >
                  <MenuItem value="">Всички</MenuItem>
                  {muscleGroups.map((group) => (
                    <MenuItem key={group} value={group}>
                      {group}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={selectedCategory}
                  onChange={(event) => setSelectedCategory(String(event.target.value))}
                >
                  <MenuItem value="">Всички</MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Button
              variant="text"
              startIcon={<ClearIcon />}
              onClick={() => {
                setSearchQuery('');
                setSelectedMuscleGroup('');
                setSelectedCategory('');
              }}
            >
              Изчисти филтрите
            </Button>

            <Stack spacing={1}>
              {filteredExerciseLibrary.map((exercise) => (
                <Card key={exercise.id} variant="outlined" sx={{ borderRadius: 3 }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                      <Box>
                        <Typography variant="subtitle1">{exercise.name}</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                          <Chip size="small" label={exercise.muscleGroup} />
                          <Chip size="small" variant="outlined" label={exercise.category} />
                          {exercise.hasVariations ? <Chip size="small" color="secondary" label="Има вариации" /> : null}
                        </Stack>
                      </Box>

                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => void handleAddExercise(exercise, selectedDayNumber)}
                      >
                        Добави
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              ))}

              {filteredExerciseLibrary.length === 0 ? (
                <Alert severity="info">Няма упражнения по тези критерии.</Alert>
              ) : null}
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExercisePickerOpen(false)}>Затвори</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={variationPickerOpen} onClose={() => setVariationPickerOpen(false)} fullScreen={fullScreenDialog} fullWidth maxWidth="sm">
        <DialogTitle>Избери вариация</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {variationSource?.name}
            </Typography>
            {variationOptions.map((exercise) => (
              <Card key={exercise.id} variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="subtitle2">
                        {exercise.variationName
                          ? `${exercise.variationName} ${variationSource?.name || ''}`.trim()
                          : exercise.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {exercise.equipment} • {exercise.difficulty}
                      </Typography>
                    </Box>
                    <Button onClick={() => appendExerciseToDay(exercise, variationTargetDay)}>Избери</Button>
                  </Stack>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setVariationPickerOpen(false);
              setVariationOptions([]);
              setVariationSource(null);
            }}
          >
            Отказ
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!exerciseEditor && !!exerciseBeingEdited}
        onClose={() => {
          setExerciseEditor(null);
          setExerciseEditorTargetDayNumber(null);
        }}
        fullScreen={fullScreenDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          component: 'form',
          onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
            event.preventDefault();
            handleSaveExerciseEditor(new FormData(event.currentTarget));
          },
        }}
      >
        <DialogTitle>Редакция на упражнение</DialogTitle>
        <DialogContent>
          {exerciseBeingEdited ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography variant="subtitle1">{exerciseBeingEdited.exercise.name}</Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField name="targetSets" label="Серии" defaultValue={exerciseBeingEdited.targetSets ?? ''} fullWidth />
                <TextField name="targetReps" label="Повторения" defaultValue={exerciseBeingEdited.targetReps ?? ''} fullWidth />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField name="targetWeight" label="Тежест" defaultValue={exerciseBeingEdited.targetWeight ?? ''} fullWidth />
                <TextField name="targetDuration" label="Време (сек)" defaultValue={exerciseBeingEdited.targetDuration ?? ''} fullWidth />
              </Stack>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField name="targetDistance" label="Дистанция" defaultValue={exerciseBeingEdited.targetDistance ?? ''} fullWidth />
                <FormControl fullWidth>
                  <InputLabel>Премести в ден</InputLabel>
                  <Select
                    name="dayNumber"
                    label="Премести в ден"
                    defaultValue={exerciseEditorTargetDayNumber ?? exerciseEditor?.dayNumber ?? selectedDayNumber}
                    onChange={(event) => setExerciseEditorTargetDayNumber(Number(event.target.value))}
                  >
                    {draftDays.map((day) => (
                      <MenuItem key={day.dayNumber} value={day.dayNumber}>
                        {day.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              {exerciseEditorDestinationDay?.rotationConfig ? (
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                    Rotation assignment (без избор = всички)
                  </FormLabel>
                  <FormGroup row>
                    {exerciseEditorDestinationDay.rotationConfig.rotationLabels.map((label, index) => (
                      <FormControlLabel
                        key={`${exerciseEditorDestinationDay.dayNumber}-ri-${index}`}
                        control={
                          <Checkbox
                            name="rotationIndexes"
                            value={String(index)}
                            defaultChecked={
                              exerciseBeingEdited.rotationIndexes != null &&
                              exerciseBeingEdited.rotationIndexes.includes(index)
                            }
                            sx={{ color: ROTATION_COLORS[index % ROTATION_COLORS.length] }}
                          />
                        }
                        label={label}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              ) : null}

              <TextField
                name="notes"
                label="Бележки"
                defaultValue={exerciseBeingEdited.notes || ''}
                fullWidth
                multiline
                minRows={3}
              />

              <FormControlLabel
                control={<Checkbox name="isFocused" defaultChecked={!!exerciseBeingEdited.isFocused} />}
                label="Маркирай като focus упражнение"
              />
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setExerciseEditor(null);
            setExerciseEditorTargetDayNumber(null);
          }}>Отказ</Button>
          <Button type="submit" variant="contained">
            Запази локално
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!rotationEditor} onClose={() => setRotationEditor(null)} fullScreen={fullScreenDialog} fullWidth maxWidth="sm">
        <DialogTitle>Rotation настройки</DialogTitle>
        <DialogContent>
          {rotationEditor ? (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Име"
                value={rotationEditor.rotationName}
                onChange={(event) =>
                  setRotationEditor((current) =>
                    current ? { ...current, rotationName: event.target.value } : current
                  )
                }
                fullWidth
              />

              {rotationEditor.rotationLabels.map((label, index) => (
                <Stack key={`${rotationEditor.dayNumber}-${index}`} direction="row" spacing={1}>
                  <TextField
                    label={`Label ${index + 1}`}
                    value={label}
                    onChange={(event) =>
                      setRotationEditor((current) => {
                        if (!current) {
                          return current;
                        }

                        const nextLabels = [...current.rotationLabels];
                        nextLabels[index] = event.target.value;
                        return {
                          ...current,
                          rotationLabels: nextLabels,
                        };
                      })
                    }
                    fullWidth
                  />
                  {rotationEditor.rotationLabels.length > 2 ? (
                    <IconButton
                      onClick={() =>
                        setRotationEditor((current) =>
                          current
                            ? {
                                ...current,
                                rotationLabels: current.rotationLabels.filter((_, labelIndex) => labelIndex !== index),
                              }
                            : current
                        )
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  ) : null}
                </Stack>
              ))}

              <Button
                variant="outlined"
                onClick={() =>
                  setRotationEditor((current) =>
                    current
                      ? {
                          ...current,
                          rotationLabels: [...current.rotationLabels, ''],
                        }
                      : current
                  )
                }
              >
                Добави label
              </Button>
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRotationEditor(null)}>Отказ</Button>
          {selectedDay?.rotationConfig ? (
            <Button color="warning" onClick={handleRemoveRotation}>
              Изтрий
            </Button>
          ) : null}
          <Button variant="contained" onClick={handleSaveRotationEditor}>
            Запази
          </Button>
        </DialogActions>
      </Dialog>
      </Box>

      <ProgramPrintView program={program} days={draftDays} />
    </Box>
  );
};

export default ProgramBuilderV2;
